// IndexedDB schema and low-level access helpers (via `idb`).
//
// Every caller must cope with `getDb()` resolving to null: IndexedDB is missing or unusable in
// some environments (older Firefox private windows, storage disabled by policy, a newer app
// version open in another tab, quota exhausted). The app then keeps working from memory –
// the outbox queues in memory, the recorder keeps chunks in memory, the cache is a Map – and
// `storageWarning()` tells the user that unsent data will not survive closing the page.
// An open that merely takes too long (seen on iOS) is not given up: once it succeeds, `getDb()`
// returns the connection and `subscribeStorageReady` listeners are told.
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { OutboxOp } from './outbox';
import type { RecorderProfile } from './recorder';

export const DB_NAME = 'baam-transkripcija';
export const DB_VERSION = 1;

/** How long we wait for `indexedDB.open` before giving up (it can hang when blocked). */
const OPEN_TIMEOUT_MS = 10_000;
/** Max number of cached documents ("doc:<id>") kept for offline use. */
const MAX_CACHED_DOCS = 150;

/** Outbox record as stored. `rev` counts in-place merges of a queued op (see outbox.ts). */
export interface StoredOutboxOp extends OutboxOp {
  rev?: number;
}

/** Metadata of a (possibly unfinished) recording whose chunks are kept for crash recovery. */
export interface RecordingRecord {
  id: string;
  startedAt: string;
  updatedAt: string;
  mimeType: string;
  profile: RecorderProfile;
  meta: Record<string, unknown>;
  /** Recorded time (excluding pauses) at the last persisted chunk. */
  durationMs: number;
  bytes: number;
}

export interface ChunkRecord {
  recordingId: string;
  index: number;
  blob: Blob;
}

export interface CacheRecord {
  /** 'features', 'settings', 'doc:<id>', 'list:<json params>', 'failed:…', 'device:owner', 'session:logout' */
  key: string;
  value: unknown;
  savedAt: string;
}

export interface BaamDB extends DBSchema {
  outbox: { key: number; value: StoredOutboxOp; indexes: { docId: string } };
  recordings: { key: string; value: RecordingRecord };
  chunks: { key: [string, number]; value: ChunkRecord; indexes: { recordingId: string } };
  cache: { key: string; value: CacheRecord };
}

export type LocalDb = IDBPDatabase<BaamDB>;

// ---------- storage warning ----------

const WARNING_UNAVAILABLE =
  'Shranjevanje v brskalnik ni na voljo (npr. v zasebnem oknu). Aplikacija deluje, vendar se ' +
  'neposlani posnetki in spremembe izgubijo, če zaprete ali osvežite stran.';
const WARNING_QUOTA =
  'V brskalniku je zmanjkalo prostora za shranjevanje. Neposlani posnetki in spremembe se izgubijo, ' +
  'če zaprete ali osvežite stran. Povežite se z internetom ali sprostite prostor na napravi.';
const WARNING_NEWER_VERSION =
  'V drugem zavihku je odprta novejša različica aplikacije. Osvežite to stran, sicer se spremembe ' +
  'ne bodo ohranile.';
const WARNING_SLOW =
  'Shramba brskalnika se ne odziva. Spremembe, ki še čakajo na pošiljanje, morda niso prikazane – ne ' +
  'brišite podatkov brskalnika. Nove spremembe se do odziva shrambe hranijo le začasno in se izgubijo, ' +
  'če zaprete ali osvežite stran. Če se to ne popravi v nekaj sekundah, zaprite brskalnik (aplikacijo) ' +
  'in ga znova odprite.';
const WARNING_NOT_SAVED =
  'Sprememb ni bilo mogoče shraniti v brskalnik. Neposlani posnetki in spremembe se izgubijo, če zaprete ' +
  'ali osvežite stran, preden se pošljejo na strežnik.';

let warning: string | null = null;
const warningListeners = new Set<(warning: string | null) => void>();

function setWarning(next: string | null): void {
  if (warning === next) return;
  warning = next;
  for (const listener of warningListeners) listener(warning);
}

/** Slovenian warning to show when data cannot be stored durably in this browser, otherwise null. */
export function storageWarning(): string | null {
  return warning;
}

export function subscribeStorageWarning(listener: (warning: string | null) => void): () => void {
  warningListeners.add(listener);
  return () => {
    warningListeners.delete(listener);
  };
}

function errorName(err: unknown): string {
  return typeof err === 'object' && err !== null && 'name' in err ? String((err as { name: unknown }).name) : '';
}

export function isQuotaError(err: unknown): boolean {
  if (errorName(err) === 'QuotaExceededError') return true;
  // Older WebKit reports quota problems as a DOMException with legacy code 22.
  return typeof DOMException !== 'undefined' && err instanceof DOMException && err.code === 22;
}

/** Record a failed IndexedDB write: quota problems become a user-visible warning. */
export function reportStorageError(err: unknown): void {
  if (isQuotaError(err)) {
    setWarning(WARNING_QUOTA);
  } else if (!warning) {
    console.warn('[localdb] IndexedDB operation failed', err);
  }
}

/**
 * Data that should have been stored durably is only kept in memory now (e.g. a queued change):
 * always tell the user, whatever the cause.
 */
export function warnNotPersisted(err: unknown): void {
  console.warn('[localdb] data kept in memory only', err);
  if (isQuotaError(err)) setWarning(WARNING_QUOTA);
  else if (!warning) setWarning(WARNING_NOT_SAVED);
}

/** Everything that was only kept in memory has been stored or sent: drop the warning about it. */
export function clearNotPersistedWarning(): void {
  if (warning === WARNING_NOT_SAVED) setWarning(null);
}

// ---------- connection ----------

let dbPromise: Promise<LocalDb | null> | null = null;
/** The connection dbPromise resolved to (null while opening or when unavailable). */
let currentDb: LocalDb | null = null;
/** Sticky for the session once opening failed; avoids mixing persistent and in-memory modes. */
let unavailable = false;
/** indexedDB.open did not answer in time; we keep waiting for it (see subscribeStorageReady). */
let slow = false;
/** Bumped by closeLocalDb(): a connection of an earlier generation that opens late is closed, not used. */
let generation = 0;
const readyListeners = new Set<() => void>();

function notifyReady(): void {
  for (const listener of [...readyListeners]) {
    try {
      listener();
    } catch (err) {
      console.error('[localdb] storage listener failed', err);
    }
  }
}

function markUnavailable(message: string, err?: unknown): null {
  unavailable = true;
  slow = false;
  setWarning(message);
  if (err !== undefined) console.warn('[localdb] IndexedDB is not available', err);
  return null;
}

function openFailed(err: unknown): null {
  if (errorName(err) === 'VersionError') return markUnavailable(WARNING_NEWER_VERSION, err);
  if (isQuotaError(err)) return markUnavailable(WARNING_QUOTA, err);
  return markUnavailable(WARNING_UNAVAILABLE, err);
}

async function open(): Promise<LocalDb | null> {
  let factory: IDBFactory | undefined;
  try {
    factory = typeof indexedDB === 'undefined' ? undefined : indexedDB;
  } catch (err) {
    // Some browsers throw SecurityError just for touching `indexedDB` when storage is blocked.
    return markUnavailable(WARNING_UNAVAILABLE, err);
  }
  if (!factory) return markUnavailable(WARNING_UNAVAILABLE);

  const gen = generation;
  let conn: LocalDb | null = null;
  let timedOut = false;
  const forget = () => {
    if (conn && currentDb === conn) {
      currentDb = null;
      dbPromise = null;
    }
  };

  const opening = openDB<BaamDB>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        const outbox = db.createObjectStore('outbox', { keyPath: 'seq', autoIncrement: true });
        outbox.createIndex('docId', 'docId');
        db.createObjectStore('recordings', { keyPath: 'id' });
        const chunks = db.createObjectStore('chunks', { keyPath: ['recordingId', 'index'] });
        chunks.createIndex('recordingId', 'recordingId');
        db.createObjectStore('cache', { keyPath: 'key' });
      }
    },
    blocked() {
      // An older connection in another tab is still open; it closes itself on 'versionchange'.
      console.warn('[localdb] database upgrade is waiting for another tab to close');
    },
    blocking() {
      // A newer app version wants to upgrade the schema: let it, and reopen lazily next time.
      conn?.close();
      forget();
    },
    terminated() {
      // The browser closed the connection (storage cleared, disk error). Reopen on next use.
      forget();
    },
  });

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<'timeout'>((resolve) => {
    timer = setTimeout(() => resolve('timeout'), OPEN_TIMEOUT_MS);
  });
  try {
    const result = await Promise.race([opening, timeout]);
    if (result === 'timeout') {
      timedOut = true;
      // Slow, not necessarily broken (seen on iOS): run from memory meanwhile, but keep waiting.
      // Data queued in earlier sessions is still in there and must not be written off.
      slow = true;
      setWarning(WARNING_SLOW);
      console.warn('[localdb] IndexedDB did not open in time; still waiting for it');
      opening.then(
        (late) => {
          if (gen !== generation || unavailable) {
            late.close();
            return;
          }
          conn = late;
          currentDb = late;
          dbPromise = Promise.resolve(late);
          slow = false;
          if (warning === WARNING_SLOW) setWarning(null);
          notifyReady();
        },
        (err: unknown) => {
          if (gen !== generation) return;
          openFailed(err);
          notifyReady();
        },
      );
      return null;
    }
    conn = result;
    if (gen === generation) currentDb = result;
    return conn;
  } catch (err) {
    if (timedOut) return null;
    return openFailed(err);
  } finally {
    clearTimeout(timer);
  }
}

/** The shared connection, or null when IndexedDB cannot be used (right now) in this browser session. */
export function getDb(): Promise<LocalDb | null> {
  if (unavailable) return Promise.resolve(null);
  dbPromise ??= open();
  return dbPromise;
}

/**
 * A request on `db` failed because the connection is closed (InvalidStateError): forget it, so the
 * next getDb() opens a fresh connection.
 */
export function invalidateDb(db: LocalDb): void {
  if (currentDb !== db) return;
  currentDb = null;
  dbPromise = null;
  try {
    db.close();
  } catch {
    // Already closed.
  }
}

/** True once IndexedDB turned out to be unusable (the app runs from memory). */
export function isStorageUnavailable(): boolean {
  return unavailable;
}

/** True while IndexedDB has not answered yet: what it holds (queued work!) is unknown meanwhile. */
export function isStorageSlow(): boolean {
  return slow;
}

/**
 * Notified when a slow open (see isStorageSlow) has finished: getDb() now resolves to the
 * connection, or to null for good when it failed after all. Returns an unsubscribe function.
 */
export function subscribeStorageReady(listener: () => void): () => void {
  readyListeners.add(listener);
  return () => {
    readyListeners.delete(listener);
  };
}

/**
 * Close the connection and reset the module state (used by tests and before deleting the
 * database). The next `getDb()` opens a fresh connection.
 */
export async function closeLocalDb(): Promise<void> {
  const pending = dbPromise;
  dbPromise = null;
  currentDb = null;
  unavailable = false;
  slow = false;
  generation++;
  memoryCache.clear();
  setWarning(null);
  if (pending) {
    const db = await pending.catch(() => null);
    db?.close();
  }
}

/**
 * A request inside a transaction whose outcome is read from `tx.done`: when the transaction aborts
 * (e.g. quota) every request rejects as well, and those rejections must not go unhandled.
 */
function quiet(request: Promise<unknown>): void {
  request.catch(() => {});
}

// ---------- cache (offline copies of server responses) ----------

/** Used when IndexedDB is unavailable, so offline fallbacks still work within the session. */
const memoryCache = new Map<string, unknown>();
let docWrites = 0;

export async function cacheGet<T>(key: string): Promise<T | undefined> {
  const db = await getDb();
  if (!db) return memoryCache.has(key) ? structuredClone(memoryCache.get(key) as T) : undefined;
  try {
    const rec = await db.get('cache', key);
    return rec?.value as T | undefined;
  } catch (err) {
    reportStorageError(err);
    return undefined;
  }
}

/** Storage has not answered yet (slow open): what it holds is unknown, not absent. */
export class StorageNotReadyError extends Error {
  constructor() {
    super('Browser storage is not ready');
    this.name = 'StorageNotReadyError';
  }
}

/**
 * Like cacheGet, but tells "no such entry" (undefined) from "could not be read" (rejects): a read
 * that fails, or storage that is still opening. Without usable IndexedDB for this session the
 * memory copy is the whole truth (nothing written earlier can be read or sent from this tab).
 */
export async function cacheGetStrict<T>(key: string): Promise<T | undefined> {
  const db = await getDb();
  if (!db) {
    if (slow) throw new StorageNotReadyError();
    return memoryCache.has(key) ? structuredClone(memoryCache.get(key) as T) : undefined;
  }
  const rec = await db.get('cache', key);
  return rec?.value as T | undefined;
}

/** Best effort: failures are reported, never thrown (the cache is only an offline convenience). */
export async function cachePut(key: string, value: unknown): Promise<void> {
  const db = await getDb();
  if (!db) {
    memoryCache.set(key, structuredClone(value));
    return;
  }
  try {
    await db.put('cache', { key, value, savedAt: new Date().toISOString() });
    if (key.startsWith('doc:') && ++docWrites % 25 === 0) await pruneDocCache(db);
  } catch (err) {
    reportStorageError(err);
  }
}

export async function cacheDelete(key: string): Promise<void> {
  const db = await getDb();
  if (!db) {
    memoryCache.delete(key);
    return;
  }
  try {
    await db.delete('cache', key);
  } catch (err) {
    reportStorageError(err);
  }
}

/** All cache entries whose key starts with `prefix`. */
export async function cacheEntries<T>(prefix: string): Promise<Array<{ key: string; value: T }>> {
  const db = await getDb();
  if (!db) {
    return [...memoryCache.entries()]
      .filter(([key]) => key.startsWith(prefix))
      .map(([key, value]) => ({ key, value: structuredClone(value) as T }));
  }
  try {
    // Keys sort lexicographically, so a prefix is the range [prefix, prefix + U+FFFF).
    const range = IDBKeyRange.bound(prefix, `${prefix}￿`);
    const records = await db.getAll('cache', range);
    return records.map((rec) => ({ key: rec.key, value: rec.value as T }));
  } catch (err) {
    reportStorageError(err);
    return [];
  }
}

async function pruneDocCache(db: LocalDb): Promise<void> {
  const tx = db.transaction('cache', 'readwrite');
  const docs = await tx.store.getAll(IDBKeyRange.bound('doc:', 'doc:￿'));
  if (docs.length > MAX_CACHED_DOCS) {
    docs.sort((a, b) => a.savedAt.localeCompare(b.savedAt));
    for (const rec of docs.slice(0, docs.length - MAX_CACHED_DOCS)) quiet(tx.store.delete(rec.key));
  }
  await tx.done;
}

/**
 * Remove every cached document and list (e.g. on logout). Queued outbox ops are kept. Resolves
 * false when the stored copies may still be there (the write failed, or storage is still opening).
 */
export async function clearContentCache(): Promise<boolean> {
  for (const key of [...memoryCache.keys()]) {
    if (key.startsWith('doc:') || key.startsWith('list:')) memoryCache.delete(key);
  }
  const db = await getDb();
  if (!db) return !slow;
  try {
    const tx = db.transaction('cache', 'readwrite');
    quiet(tx.store.delete(IDBKeyRange.bound('doc:', 'doc:￿')));
    quiet(tx.store.delete(IDBKeyRange.bound('list:', 'list:￿')));
    await tx.done;
    return true;
  } catch (err) {
    reportStorageError(err);
    return false;
  }
}

// ---------- recordings (crash recovery) ----------

const chunkRange = (recordingId: string) =>
  IDBKeyRange.bound([recordingId, 0], [recordingId, Number.MAX_SAFE_INTEGER]);

/**
 * Write (or update) a recording record and optionally one chunk in a single transaction.
 * Resolves false when IndexedDB is unavailable; rejects when the write itself fails.
 */
export async function saveRecordingChunk(record: RecordingRecord, chunk?: ChunkRecord): Promise<boolean> {
  const db = await getDb();
  if (!db) return false;
  const tx = db.transaction(['recordings', 'chunks'], 'readwrite');
  quiet(tx.objectStore('recordings').put(record));
  if (chunk) quiet(tx.objectStore('chunks').put(chunk));
  await tx.done;
  return true;
}

/** With `strict`, storage that is still opening rejects instead of answering "none". */
export async function listRecordingRecords(opts: { strict?: boolean } = {}): Promise<RecordingRecord[]> {
  const db = await getDb();
  if (!db) {
    if (opts.strict && slow) throw new StorageNotReadyError();
    return [];
  }
  return db.getAll('recordings');
}

export async function getRecordingRecord(id: string): Promise<RecordingRecord | undefined> {
  const db = await getDb();
  if (!db) return undefined;
  return db.get('recordings', id);
}

/** Chunks of a recording in index order. */
export async function getRecordingChunks(id: string): Promise<ChunkRecord[]> {
  const db = await getDb();
  if (!db) return [];
  // The primary key is [recordingId, index], so a key range scan is already in index order.
  return db.getAll('chunks', chunkRange(id));
}

export async function deleteRecordingData(id: string): Promise<void> {
  const db = await getDb();
  if (!db) return;
  const tx = db.transaction(['recordings', 'chunks'], 'readwrite');
  quiet(tx.objectStore('recordings').delete(id));
  quiet(tx.objectStore('chunks').delete(chunkRange(id)));
  await tx.done;
}

// ---------- recordings in use (never offered for recovery, never deleted by others) ----------
//
// A recording is "held" while a recorder session writes it, or while the outbox needs its
// crash-recovery copy because the queued upload is only kept in memory. Holds are known in this
// tab directly and in other tabs through a Web Lock per recording (where supported; elsewhere the
// recorder's heartbeat keeps `updatedAt` fresh, which the recovery dialog also respects).

const LOCK_PREFIX = 'baam-rec-';
const localHolds = new Map<string, number>();

/** Mark a recording as in use until the returned function is called (idempotent). */
export function holdRecording(id: string): () => void {
  localHolds.set(id, (localHolds.get(id) ?? 0) + 1);
  let released = false;
  let unlock: (() => void) | null = null;
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (typeof locks?.request === 'function') {
    try {
      // Shared: holders never wait for each other; the lock only has to be visible in query().
      void locks
        .request(`${LOCK_PREFIX}${id}`, { mode: 'shared' }, () =>
          released ? undefined : new Promise<void>((resolve) => (unlock = resolve)),
        )
        .catch(() => {});
    } catch {
      // Web Locks unusable here (e.g. opaque origin): the in-tab registry still works.
    }
  }
  return () => {
    if (released) return;
    released = true;
    const count = (localHolds.get(id) ?? 1) - 1;
    if (count > 0) localHolds.set(id, count);
    else localHolds.delete(id);
    unlock?.();
  };
}

/** True when this tab holds the recording (see holdRecording). */
export function isRecordingHeldHere(id: string): boolean {
  return localHolds.has(id);
}

/** Ids of recordings held by this tab or (where Web Locks exist) by any other tab. */
export async function recordingsInUse(): Promise<Set<string>> {
  const ids = new Set(localHolds.keys());
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (typeof locks?.query === 'function') {
    try {
      const snapshot = await locks.query();
      for (const lock of [...(snapshot.held ?? []), ...(snapshot.pending ?? [])]) {
        if (lock.name?.startsWith(LOCK_PREFIX)) ids.add(lock.name.slice(LOCK_PREFIX.length));
      }
    } catch {
      // Not available: only this tab's holds are known.
    }
  }
  return ids;
}

/** Recovery id of a Blob handed out by the recorder (stop() or recoverRecording()). */
const recordingBlobs = new WeakMap<Blob, string>();

export function rememberRecordingBlob(blob: Blob, recoveryId: string): void {
  recordingBlobs.set(blob, recoveryId);
}

/** The recovery id of the crash-recovery copy of `blob`, if the blob came from the recorder. */
export function recordingIdOfBlob(blob: Blob): string | undefined {
  return recordingBlobs.get(blob);
}
